import { useEffect, type RefObject } from "react";
import "./formHelp.css";

/** Help content for one field: a plain body string, or titled content. */
export type FieldHelp = string | { title?: string; body: string };

/**
 * Popover help for a form, keyed by the field's control `name` (preferred —
 * stable across label rewording, dynamic suffixes like "(required)", and
 * i18n) or by the exact visible label text (fallback for nameless controls).
 * Fields without a matching key are simply left undecorated.
 */
export type FormHelpMap = Record<string, FieldHelp>;

const TRIGGER_CLASS = "afsh-help-trigger";
const POP_CLASS = "afsh-help-pop";
const TITLE_CLASS = "afsh-help-title";
const BODY_CLASS = "afsh-help-body";

let popoverSeq = 0;

/**
 * Attaches the Form Help convention to a form: every `<label>` inside the
 * form whose control name (or label text) matches a {@link FormHelpMap} key
 * gets a "?" trigger and a native popover next to its label text — no help
 * text or markup in the field components themselves.
 *
 * Re-applies when the form's content mutates (multi-page forms swapping
 * sections) and is idempotent, so StrictMode double-mounts and repeated
 * passes are safe. Cleanup only disconnects the observer; injected nodes die
 * with the form element.
 *
 * Known limits: a key matching a radio/chip group would decorate the first
 * chip's label (omit such groups), and decorated DOM lives outside React's
 * reconciliation — tolerated as progressive enhancement, not synchronised.
 *
 * @param formRef - Ref of the `<form>` (or any container) to decorate.
 * @param help - Key → help content map; omit or pass empty to disable.
 */
export function useFormHelp(formRef: RefObject<HTMLElement | null>, help?: FormHelpMap): void {
  useEffect(() => {
    const root = formRef.current;
    if (!root || !help) return;
    const entries = Object.entries(help);
    if (entries.length === 0) return;

    let scheduled = false;
    let applying = false;
    const apply = (): void => {
      scheduled = false;
      applying = true;
      for (const [key, fieldHelp] of entries) decorate(root, key, fieldHelp);
      applying = false;
    };
    // Our own insertions are subtree mutations too; the applying guard keeps
    // the observer from scheduling a pass on them (decoration is idempotent
    // regardless).
    const schedule = (): void => {
      if (scheduled || applying) return;
      scheduled = true;
      queueMicrotask(apply);
    };

    apply();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [formRef, help]);
}

/** Finds the label to decorate for one map key and injects the help UI. */
function decorate(root: HTMLElement, key: string, help: FieldHelp): void {
  const label = resolveLabel(root, key);
  if (!label || label.querySelector(`:scope .${TRIGGER_CLASS}`)) return;

  const anchorSpan = firstTextOnlySpan(label);
  const labelText = (anchorSpan ?? label).textContent?.trim() || key;
  // A wrapping label without a text span would bury the trigger after its
  // input; only plain text labels (e.g. file-input buttons) get the fallback.
  const anchor = anchorSpan ?? (label.querySelector("input, select, textarea") ? null : label);
  if (!anchor) return;

  const { trigger, pop } = buildHelpUi(labelText, help);
  anchor.appendChild(trigger);
  anchor.appendChild(pop);

  const control = labelledControl(root, label);
  if (control) {
    const existing = control.getAttribute("aria-describedby");
    control.setAttribute("aria-describedby", existing ? `${existing} ${pop.id}` : pop.id);
  }
}

/**
 * Resolves a map key to a label: via the named/id'd control's own label
 * (nested or `for=`-associated), else via exact label-text match.
 */
function resolveLabel(root: HTMLElement, key: string): HTMLLabelElement | null {
  const control = root.querySelector(`[name="${CSS.escape(key)}"], #${CSS.escape(key)}`);
  const label =
    control?.closest("label") ??
    (control?.id
      ? root.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(control.id)}"]`)
      : null);
  if (label) return label;
  for (const candidate of root.querySelectorAll("label")) {
    if (candidate.textContent?.trim() === key) return candidate;
  }
  return null;
}

/** The control to receive `aria-describedby` for a decorated label. */
function labelledControl(root: HTMLElement, label: HTMLLabelElement): HTMLElement | null {
  return (
    label.querySelector("input, select, textarea") ??
    (label.htmlFor ? root.querySelector(`#${CSS.escape(label.htmlFor)}`) : null)
  );
}

/** The label's visible text span: the first text-only `<span>` in the label. */
function firstTextOnlySpan(label: HTMLLabelElement): HTMLSpanElement | null {
  for (const span of label.querySelectorAll("span")) {
    if (span.childElementCount === 0 && span.textContent?.trim()) return span;
  }
  return null;
}

/** Builds the "?" trigger and its native popover (top layer, light dismiss). */
function buildHelpUi(
  labelText: string,
  help: FieldHelp,
): { trigger: HTMLButtonElement; pop: HTMLDivElement } {
  const pop = document.createElement("div");
  pop.id = `afsh-help-${++popoverSeq}`;
  pop.className = POP_CLASS;
  pop.popover = "auto";

  const title = typeof help === "string" ? undefined : help.title?.trim();
  if (title) {
    const heading = document.createElement("div");
    heading.className = TITLE_CLASS;
    heading.textContent = title;
    pop.appendChild(heading);
  }
  const body = document.createElement("div");
  body.className = BODY_CLASS;
  body.textContent = typeof help === "string" ? help : help.body;
  pop.appendChild(body);

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = TRIGGER_CLASS;
  trigger.textContent = "?";
  trigger.setAttribute("aria-label", `Help: ${labelText}`);
  trigger.setAttribute("aria-haspopup", "dialog");
  trigger.setAttribute("popovertarget", pop.id);

  // Native popovers are centred by the UA sheet; pin to the trigger on open.
  pop.addEventListener("toggle", (event) => {
    if ((event as ToggleEvent).newState === "open") positionPopover(pop, trigger);
  });

  return { trigger, pop };
}

/** Places the open popover under its trigger, clamped to the viewport. */
function positionPopover(pop: HTMLDivElement, trigger: HTMLButtonElement): void {
  const t = trigger.getBoundingClientRect();
  const p = pop.getBoundingClientRect();
  const margin = 8;
  const left = Math.max(
    margin,
    Math.min(t.left, window.innerWidth - p.width - margin),
  );
  let top = t.bottom + 6;
  if (top + p.height > window.innerHeight - margin) top = t.top - p.height - 6;
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(Math.max(margin, top))}px`;
}
